import { useEffect, useState } from 'react'
import {
  Grid3x3,
  Loader2,
  Mic,
  MicOff,
  Pause,
  PhoneOff,
  Play,
  Repeat,
  Save,
  UserPlus,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useCallStore } from '@/stores/useCallStore'
import { useCallActions } from '@/hooks/useCallActions'
import { useAddContactNote } from '@/api/contacts'
import { AnnonceBar } from './AnnonceBar'

interface Props {
  phoneNumber: string
  darkMode?: boolean
}

/**
 * Panneau de contrôle d'appel + Quick notes, rendu sous la timeline d'un
 * contact pendant un appel actif avec ce contact. Pattern Aircall/Dialpad.
 *
 * S'affiche UNIQUEMENT si `isCallConnected` et `callerPhoneNumber` correspond
 * au `phoneNumber` du contact actif.
 */
export function InCallPanel({ phoneNumber, darkMode = false }: Props) {
  const { t } = useTranslation()
  const isCallConnected = useCallStore((s) => s.isCallConnected)
  const isCallMuted = useCallStore((s) => s.isCallMuted)
  const callDuration = useCallStore((s) => s.callDuration)
  const isOnHold = useCallStore((s) => s.isOnHold)
  const isHoldLoading = useCallStore((s) => s.isHoldLoading)
  // Announced transfer under way: hold and another transfer wait for its end.
  const enAnnonce = useCallStore((s) => !!s.annonce?.etape)
  const callerPhoneNumber = useCallStore((s) => s.callerPhoneNumber)
  const callerContactName = useCallStore((s) => s.callerContactName)
  const isInternalCall = useCallStore((s) => s.isInternalCall)
  // Call with several people: no other transfer; leaving keeps the others on the line.
  const participants = useCallStore((s) => s.participants)
  const ajoutEnCours = useCallStore((s) => s.ajoutEnCours)
  const aPlusieurs = participants.length > 0
  const quitte = participants.some((p) => p.en_ligne && !p.externe)

  const { toggleMute, hangUp, sendDTMF, hold, unhold, openTransferModal, openAddModal } =
    useCallActions()

  const setHasInlineCallUI = useCallStore((s) => s.setHasInlineCallUI)
  const addNote = useAddContactNote(phoneNumber)

  const [showKeypad, setShowKeypad] = useState(false)
  const [noteText, setNoteText] = useState('')

  // Signale au widget flottant qu'une UI inline est visible pour CE contact.
  // Le widget s'auto-cache → pas de doublon. Reset à false au unmount.
  const inlineVisible = isCallConnected && callerPhoneNumber === phoneNumber
  useEffect(() => {
    if (inlineVisible) {
      setHasInlineCallUI(true)
      return () => setHasInlineCallUI(false)
    }
  }, [inlineVisible, setHasInlineCallUI])

  // Ne rend que pendant l'appel avec CE contact
  if (!isCallConnected) return null
  if (callerPhoneNumber !== phoneNumber) return null

  const handleSaveNote = async () => {
    if (!noteText.trim() || addNote.isPending) return
    try {
      await addNote.mutateAsync(noteText.trim())
      setNoteText('')
      toast.success(t('phone.noteSaved', 'Note enregistrée'))
    } catch {
      toast.error(t('common.error', 'Erreur'))
    }
  }

  const border = darkMode ? 'border-slate-800' : 'border-slate-200'
  const containerBg = darkMode ? 'bg-slate-900' : 'bg-white'
  const inputBg = darkMode ? 'bg-slate-800' : 'bg-slate-50'
  const textPrimary = darkMode ? 'text-white' : 'text-slate-900'
  const textMuted = darkMode ? 'text-slate-400' : 'text-slate-500'

  return (
    <div className={cn('flex-shrink-0 border-t', border, containerBg)}>
      {/* Header : statut + timer */}
      <div
        className={cn(
          'px-4 py-2 flex items-center justify-between border-b',
          border,
          darkMode ? 'bg-emerald-500/10' : 'bg-emerald-50/60',
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              'h-2 w-2 rounded-full flex-shrink-0',
              isOnHold ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse',
            )}
          />
          <span
            className={cn(
              'text-xs font-medium',
              darkMode ? 'text-emerald-400' : 'text-emerald-700',
            )}
          >
            {isOnHold
              ? t('phone.onHold', 'En attente')
              : t('phone.inProgress', 'Appel en cours')}
          </span>
          {callerContactName && (
            <span className={cn('text-xs truncate', textMuted)}>
              · {callerContactName}
            </span>
          )}
        </div>
        <span
          className={cn(
            'text-sm font-mono font-bold flex-shrink-0',
            darkMode ? 'text-emerald-400' : 'text-emerald-700',
          )}
        >
          {formatDuration(callDuration)}
        </span>
      </div>

      <AnnonceBar darkMode={darkMode} />

      {/* Quick notes */}
      <div className="px-4 py-3">
        <label
          className={cn(
            'text-[10px] font-semibold uppercase tracking-wider mb-1.5 block',
            textMuted,
          )}
        >
          {t('phone.quickNotes', 'Notes rapides')}
        </label>
        <div className="flex items-start gap-2">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t(
              'phone.quickNotesPlaceholder',
              'Tapez vos notes pendant l\'appel...',
            )}
            rows={2}
            className={cn(
              'flex-1 text-sm rounded-lg border px-3 py-2 resize-none outline-none focus:ring-1 focus:ring-indigo-500',
              inputBg,
              border,
              textPrimary,
              darkMode ? 'placeholder-slate-600' : 'placeholder-slate-400',
            )}
          />
          <button
            type="button"
            onClick={() => void handleSaveNote()}
            disabled={!noteText.trim() || addNote.isPending}
            className={cn(
              'flex items-center justify-center gap-1 rounded-lg px-3 h-9 text-xs font-medium transition-colors disabled:opacity-50 flex-shrink-0',
              noteText.trim()
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : darkMode
                  ? 'bg-slate-800 text-slate-500'
                  : 'bg-slate-100 text-slate-400',
            )}
          >
            {addNote.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {t('common.save', 'Save')}
          </button>
        </div>
      </div>

      {/* Rangée de boutons d'action — centrés */}
      <div className={cn('px-4 py-4 border-t', border)}>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <ActionButton
            onClick={toggleMute}
            active={isCallMuted}
            tone={isCallMuted ? 'destructive' : 'default'}
            darkMode={darkMode}
            label={
              isCallMuted ? t('phone.unmute', 'Mic off') : t('phone.mute', 'Mute')
            }
            icon={
              isCallMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />
            }
          />

          <ActionButton
            onClick={() => setShowKeypad((v) => !v)}
            active={showKeypad}
            darkMode={darkMode}
            label={t('phone.keypad', 'Clavier')}
            icon={<Grid3x3 className="w-5 h-5" />}
          />

          <ActionButton
            onClick={() => void (isOnHold ? unhold() : hold())}
            active={isOnHold}
            disabled={isHoldLoading || enAnnonce}
            darkMode={darkMode}
            label={
              isOnHold ? t('phone.resume', 'Reprendre') : t('phone.hold', 'Pause')
            }
            icon={
              isOnHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />
            }
          />

          <ActionButton
            onClick={openAddModal}
            disabled={isOnHold || enAnnonce || !!ajoutEnCours || isInternalCall}
            darkMode={darkMode}
            label={t('phone.ajout.label', 'Ajouter')}
            icon={<UserPlus className="w-5 h-5" />}
          />

          <ActionButton
            onClick={openTransferModal}
            disabled={isOnHold || enAnnonce || aPlusieurs}
            darkMode={darkMode}
            label={t('phone.transferLabel', 'Transfert')}
            icon={<Repeat className="w-5 h-5" />}
          />

          <button
            type="button"
            onClick={hangUp}
            disabled={isOnHold}
            className="flex flex-col items-center justify-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-xl h-14 w-14 transition-colors disabled:opacity-50 shadow-md shadow-red-500/30"
            title={quitte ? t('phone.ajout.leaveHint', 'Les autres restent en ligne') : t('phone.hangup', 'Raccrocher')}
          >
            <PhoneOff className="w-5 h-5" />
            <span className="text-[9px] font-semibold leading-none">
              {quitte ? t('phone.ajout.leave', 'Quitter') : t('phone.hangup', 'Raccrocher')}
            </span>
          </button>
        </div>
      </div>

      {/* Keypad DTMF compact, collé sous les boutons */}
      {showKeypad && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-3 gap-1 max-w-[200px] mx-auto">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(
              (digit) => (
                <button
                  key={digit}
                  type="button"
                  onClick={() => sendDTMF(digit)}
                  className={cn(
                    'h-8 rounded-md text-sm font-semibold tabular-nums transition-colors',
                    darkMode
                      ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                      : 'bg-white hover:bg-slate-50 text-slate-900 border border-slate-200',
                  )}
                >
                  {digit}
                </button>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface ActionButtonProps {
  onClick: () => void
  icon: React.ReactNode
  label: string
  active?: boolean
  disabled?: boolean
  tone?: 'default' | 'destructive'
  darkMode: boolean
}

function ActionButton({
  onClick,
  icon,
  label,
  active,
  disabled,
  tone = 'default',
  darkMode,
}: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={cn(
        'h-14 w-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        tone === 'destructive' && active
          ? 'bg-red-500 text-white shadow-md shadow-red-500/30'
          : active
            ? 'bg-amber-500 text-white shadow-md shadow-amber-500/30'
            : darkMode
              ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700'
              : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 shadow-sm',
      )}
    >
      {icon}
      <span className="text-[9px] font-semibold leading-none">{label}</span>
    </button>
  )
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}
